import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { format, subDays, subMonths, subWeeks } from 'date-fns';

import { Typography, Select, OptionItem } from '@app/components';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { EmailApi } from '@app/domain/email/api/EmailApi';
import { EEmailReportGranularity } from '@app/swagger-types';
import { SelectChangeEvent } from '@mui/material';

import { EmailReportKpiCards } from './components/EmailReportKpiCards';
import { EmailReportVolumeChart } from './components/EmailReportVolumeChart';
import { EmailReportPercentChart } from './components/EmailReportPercentChart';
import { EmailReportMailTypeTable } from './components/EmailReportMailTypeTable';
import { EmailNotSentTable } from './components/EmailNotSentTable';
import { EmailCard } from '../shared/EmailCard';

const DATE_FORMAT = 'yyyy-MM-dd';

// admin-emailbeheer blok B (opdracht §4's own table): default range per granularity - reset
// whenever the user switches Dag/Week/Maand, same as the opdracht's own worked example
// ("maand, laatste 12" as a shareable link) implies.
const DEFAULT_DAYS_FOR: Record<EEmailReportGranularity, () => Date> = {
  [EEmailReportGranularity.DAY]: () => subDays(new Date(), 14),
  [EEmailReportGranularity.WEEK]: () => subWeeks(new Date(), 12),
  [EEmailReportGranularity.MONTH]: () => subMonths(new Date(), 12),
};

export const AdminEmailReportPage: React.FC = () => {
  const [granularity, setGranularity] = useQueryParam(
    'granularity',
    withDefault(StringParam, EEmailReportGranularity.WEEK)
  );
  const [from, setFrom] = useQueryParam(
    'from',
    withDefault(StringParam, format(DEFAULT_DAYS_FOR[EEmailReportGranularity.WEEK](), DATE_FORMAT))
  );
  const [to, setTo] = useQueryParam('to', withDefault(StringParam, format(new Date(), DATE_FORMAT)));

  const handleGranularityChange = (next: EEmailReportGranularity) => {
    setGranularity(next);
    setFrom(format(DEFAULT_DAYS_FOR[next](), DATE_FORMAT));
    setTo(format(new Date(), DATE_FORMAT));
  };

  const summaryQuery = useQuery(['email-report-summary', from, to], () =>
    EmailApi.adminGetEmailReportSummary(from as string, to as string)
  );

  const seriesQuery = useQuery(['email-report-series', from, to, granularity], () =>
    EmailApi.adminGetEmailReportSeries(from as string, to as string, granularity as EEmailReportGranularity)
  );

  const rangeLabel = useMemo(() => `${from} — ${to}`, [from, to]);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Typography variant="xs" className="text-gray-400">
          {rangeLabel}
        </Typography>
        <Select
          size="small"
          value={granularity}
          onChange={(e: SelectChangeEvent<unknown>) =>
            handleGranularityChange(e.target.value as EEmailReportGranularity)
          }
        >
          <OptionItem value={EEmailReportGranularity.DAY}>Day</OptionItem>
          <OptionItem value={EEmailReportGranularity.WEEK}>Week</OptionItem>
          <OptionItem value={EEmailReportGranularity.MONTH}>Month</OptionItem>
        </Select>
      </div>

      <div className="flex flex-col gap-6 pt-5">
        <ContentLoader loading={summaryQuery.isLoading} error={summaryQuery.error} />

        {summaryQuery.data && (
          <EmailReportKpiCards
            kpis={summaryQuery.data.kpis}
            percentagesMeasuredSince={summaryQuery.data.percentagesMeasuredSince}
          />
        )}

        <EmailCard title={`Sent per ${granularity.toLowerCase()}, by category`}>
          <EmailReportVolumeChart buckets={seriesQuery.data?.buckets ?? []} loading={seriesQuery.isLoading} />
        </EmailCard>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <EmailCard title="Bounce rate" subtitle="Hard limit generally held at 5%">
            <EmailReportPercentChart
              buckets={seriesQuery.data?.buckets ?? []}
              loading={seriesQuery.isLoading}
              dataKey="bouncePercent"
              hardLimit={5}
            />
          </EmailCard>
          <EmailCard title="Complaint rate" subtitle="Gmail/Yahoo bulk-sender requirement: under 0.3%">
            <EmailReportPercentChart
              buckets={seriesQuery.data?.buckets ?? []}
              loading={seriesQuery.isLoading}
              dataKey="complaintPercent"
              hardLimit={0.3}
            />
          </EmailCard>
        </div>

        {summaryQuery.data && <EmailReportMailTypeTable rows={summaryQuery.data.perMailType} />}
        {summaryQuery.data && <EmailNotSentTable rows={summaryQuery.data.notSent} />}
      </div>
    </>
  );
};
